import { Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";

import { getEntityTypeEntry } from "../../types/entityRegistry";
import { ChainCardRef } from "../ValueChain/valueChainState";
import { useMakeRoot } from "./kindChain";

interface MakeRootButtonProps {
  // The kind or the class as a root of the value chain
  root: ChainCardRef;
}

/**
 * "Make root" in the header of the kind and the class views: re-centres the
 * chain the page shows on it in place, or opens the value chain page with it
 * as the root from any other page.
 */
const MakeRootButton: React.FC<MakeRootButtonProps> = ({ root }) => {
  const makeRoot = useMakeRoot();
  return (
    <Button
      size="small"
      variant="outlined"
      color="inherit"
      onClick={() => makeRoot(root)}
      sx={{
        height: 32,
        mr: 0.75,
        px: 1.5,
        flexShrink: 0,
        whiteSpace: "nowrap",
        textTransform: "none",
        fontSize: "0.8125rem",
        fontWeight: 500,
        color: "text.primary",
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.1),
        "&:hover": {
          borderColor: (theme) => alpha(theme.palette.text.primary, 0.3),
          backgroundColor: "action.hover",
        },
      }}
    >
      Make root
    </Button>
  );
};

/**
 * The header's extra actions of a kind or a class: "make root", where the
 * registry entry of the type allows it.
 */
export const makeRootAction = (
  entityType: ChainCardRef["type"],
  root: ChainCardRef,
): React.ReactNode =>
  getEntityTypeEntry(entityType)?.allowsMakeRoot ? (
    <MakeRootButton root={root} />
  ) : null;

export default MakeRootButton;
